import React, { useEffect, useState } from 'react';
import { withRouter } from "react-router-dom";
import Api from '../Services/Api';
import Alert from '../Services/Alert';

/**
 * Payment gateway management (feature C).
 *
 * Several gateways can be active for Add Money at the same time. For a given deposit amount
 * the gateways whose range 1 OR range 2 contains it are candidates, and the lowest Priority
 * wins. Leaving every range blank means "no restriction" — the gateway accepts any amount,
 * which is how gateways behaved before ranges existed.
 *
 * Withdraw stays single-gateway: the API still resolves payouts with findOne({withdraw:true}).
 */
export const PaymentGateways = (props) => {
    const [list, setList] = useState([]);
    const [edits, setEdits] = useState({}); // { [id]: {priority,min_amount,max_amount,min_amount2,max_amount2} }

    const blankRow = (g) => ({
        priority: g.priority ?? 0,
        min_amount: g.min_amount ?? '',
        max_amount: g.max_amount ?? '',
        min_amount2: g.min_amount2 ?? '',
        max_amount2: g.max_amount2 ?? '',
        bonus_percentage: g.bonus_percentage ?? '',
    });

    function load() {
        Api.get('payment-gateway').then(resp => {
            const r = resp.data;
            if (r.status != 1) { Alert.error(r.message); return; }
            const rows = r.data || [];
            setList(rows);
            const e = {};
            rows.forEach(g => { e[g._id] = blankRow(g); });
            setEdits(e);
        }).catch(err => Alert.error(err.message));
    }

    useEffect(() => { load(); }, []);

    function setField(id, field, value) {
        setEdits(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
    }

    function toggleStatus(g, type) {
        const isOn = type === 'pay_in' ? g.status : g.withdraw;
        const next = isOn ? '0' : '1';
        const what = type === 'pay_in' ? 'Add Money' : 'Withdraw';
        Alert.warning(`${isOn ? 'Deactivate' : 'Activate'} ${g.name} for ${what}?`).then(result => {
            if (!result.isConfirmed) return;
            Api.post('payment-gateway/status', { id: g._id, status: next, type }).then(r => r.data).then(r => {
                if (r.status) { Alert.success(r.message); load(); }
                else Alert.error(r.message);
            }).catch(err => Alert.error(err.message));
        });
    }

    function saveRanges(g) {
        const e = edits[g._id] || {};
        Api.post('payment-gateway/ranges', {
            id: g._id,
            priority: e.priority === '' ? 0 : e.priority,
            min_amount: e.min_amount,
            max_amount: e.max_amount,
            min_amount2: e.min_amount2,
            max_amount2: e.max_amount2,
            bonus_percentage: e.bonus_percentage,
        }).then(r => r.data).then(r => {
            if (r.status) { Alert.success(r.message); load(); }
            else Alert.error(r.message);
        }).catch(err => Alert.error(err.message));
    }

    const num = (id, field, placeholder) => (
        <input type="number" min="0" className="form-control form-control-sm"
            style={{ width: '90px', display: 'inline-block' }}
            placeholder={placeholder}
            value={edits[id]?.[field] ?? ''}
            onChange={ev => setField(id, field, ev.target.value)} />
    );

    return (
        <section className="main-content marketadd_main_sec">
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="card">
                                <div className="card-body">
                                    <h4 className="card-title mb-2">Payment Gateways</h4>
                                    <p className="text-muted">
                                        Several gateways can be active for Add Money at once. A deposit goes to the
                                        active gateway whose range covers the amount; if more than one matches, the
                                        lowest <strong>Priority</strong> wins. Leave ranges blank for no restriction.
                                        The second range is optional and must start above the first range&rsquo;s maximum.
                                        <br />
                                        <strong>Bonus %</strong> is the deposit bonus for this gateway. Leave it blank to
                                        use the global percentage from Settings; enter <strong>0</strong> to pay no bonus
                                        on this gateway.
                                    </p>
                                    <div className="table-responsive">
                                        <table className="table align-middle table-nowrap mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Name</th>
                                                    <th>Add Money</th>
                                                    <th>Withdraw</th>
                                                    <th>Priority</th>
                                                    <th>Range 1 (min / max)</th>
                                                    <th>Range 2 (optional)</th>
                                                    <th>Bonus %</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {list && list.length > 0 ? list.map((g, key) => (
                                                    <tr key={key}>
                                                        <td>{g.name}<br /><small className="text-muted">{g.code}</small></td>
                                                        <td>
                                                            <span className={g.status ? 'text-success' : 'text-danger'}
                                                                style={{ cursor: 'pointer' }}
                                                                title="Click to toggle"
                                                                onClick={() => toggleStatus(g, 'pay_in')}>
                                                                {g.status ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={g.withdraw ? 'text-success' : 'text-danger'}
                                                                style={{ cursor: 'pointer' }}
                                                                title="Only one gateway can handle withdrawals"
                                                                onClick={() => toggleStatus(g, 'pay_out')}>
                                                                {g.withdraw ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td>{num(g._id, 'priority', '0')}</td>
                                                        <td>
                                                            {num(g._id, 'min_amount', 'min')} &nbsp;–&nbsp; {num(g._id, 'max_amount', 'max')}
                                                        </td>
                                                        <td>
                                                            {num(g._id, 'min_amount2', 'min')} &nbsp;–&nbsp; {num(g._id, 'max_amount2', 'max')}
                                                        </td>
                                                        <td>{num(g._id, 'bonus_percentage', 'global')}</td>
                                                        <td>
                                                            <button className="btn btn-sm btn-primary" onClick={() => saveRanges(g)}>Save</button>
                                                        </td>
                                                    </tr>
                                                )) : (
                                                    <tr><td colSpan="8" className="text-center">No payment gateways</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default withRouter(PaymentGateways);
